<template>
    <div class="quotation-edit-page">
        <app-sub-title text="Editar Cotización" />
        
        <div class="quotation-form-container" v-if="!loading">
            <div class="form-section mb-4">
                <h5>Cliente</h5>
                <app-form-select2-input
                    v-model="quotation.client_id"
                    label="Cliente Prospecto"
                    :options="clientOptions"
                    :has-error="$v.quotation.client_id.$error"
                    error-message="Debe seleccionar un cliente"
                    required
                    @input="onClientSelected"
                />
                
                <div v-if="selectedClient" class="client-info mt-3 p-3 bg-light rounded">
                    <h6>Información del Cliente</h6>
                    <div class="row">
                        <div class="col-md-6">
                            <p><strong>Nombre:</strong> {{ selectedClient.name }}</p>
                            <p><strong>Documento:</strong> {{ selectedClient.document || 'N/A' }}</p>
                        </div>
                        <div class="col-md-6">
                            <p><strong>Email:</strong> {{ selectedClient.email || 'N/A' }}</p>
                            <p><strong>Teléfono:</strong> {{ selectedClient.phone || 'N/A' }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-section mb-4">
                <h5>Información General</h5>
                <app-form-text-input
                    v-model="quotation.title"
                    label="Título"
                    :has-error="$v.quotation.title.$error"
                    error-message="Debe ingresar un título"
                    required
                />
                <app-form-textarea-input
                    v-model="quotation.description"
                    label="Descripción"
                    :rows="3"
                />
                <app-form-select-input
                    v-model="quotation.status"
                    label="Estado"
                    :options="statusOptions"
                />
            </div>

            <!-- Categories Accordion -->
            <div class="form-section mb-4">
                <h5>Items de Cotización</h5>
                <div class="mb-3">
                    <app-form-select2-input
                        :value="selectedKitId"
                        label="Seleccionar Kit"
                        :options="kitOptions"
                        placeholder="Seleccione un kit para agregar todos sus items"
                        @input="onKitSelected"
                    />
                </div>
                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-equipment-cctv variant="info">
                            Equipos sistemas de CCTV
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('equipment-cctv') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-equipment-cctv" visible accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <category-items-tab
                                category-slug="equipment-cctv"
                                category-name="Equipos sistemas de CCTV"
                                :items="categoryItems['equipment-cctv']"
                                :selected-items="quotationLines['equipment-cctv']"
                                :multiplier="settings.equipos"
                                @add-item="addItem"
                                @remove-item="removeItem"
                                @update-item="updateItem"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-equipment-access-control variant="info">
                            Equipos sistema control de acceso
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('equipment-access-control') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-equipment-access-control" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <category-items-tab
                                category-slug="equipment-access-control"
                                category-name="Equipos sistema control de acceso"
                                :items="categoryItems['equipment-access-control']"
                                :selected-items="quotationLines['equipment-access-control']"
                                :multiplier="settings.equipos"
                                @add-item="addItem"
                                @remove-item="removeItem"
                                @update-item="updateItem"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-equipment-network variant="info">
                            Equipos de red
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('equipment-network') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-equipment-network" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <category-items-tab
                                category-slug="equipment-network"
                                category-name="Equipos de red"
                                :items="categoryItems['equipment-network']"
                                :selected-items="quotationLines['equipment-network']"
                                :multiplier="settings.equipos"
                                @add-item="addItem"
                                @remove-item="removeItem"
                                @update-item="updateItem"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-equipment-alarm variant="info">
                            Equipos sistema de alarma
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('equipment-alarm') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-equipment-alarm" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <category-items-tab
                                category-slug="equipment-alarm"
                                category-name="Equipos sistema de alarma"
                                :items="categoryItems['equipment-alarm']"
                                :selected-items="quotationLines['equipment-alarm']"
                                :multiplier="settings.equipos"
                                @add-item="addItem"
                                @remove-item="removeItem"
                                @update-item="updateItem"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-material variant="info">
                            Materiales
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('material') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-material" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <category-items-tab
                                category-slug="material"
                                category-name="Materiales"
                                :items="categoryItems.material"
                                :selected-items="quotationLines.material"
                                :multiplier="settings.materiales"
                                @add-item="addItem"
                                @remove-item="removeItem"
                                @update-item="updateItem"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-labor variant="info">
                            Mano de Obra
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('labor') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-labor" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <category-items-tab
                                category-slug="labor"
                                category-name="Mano de Obra"
                                :items="categoryItems.labor"
                                :selected-items="quotationLines.labor"
                                :multiplier="settings['mano-obra']"
                                @add-item="addItem"
                                @remove-item="removeItem"
                                @update-item="updateItem"
                                @add-manual-labor-item="addManualLaborItem"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-license variant="info">
                            Licencia
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('license') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-license" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <category-items-tab
                                category-slug="license"
                                category-name="Licencia"
                                :items="categoryItems.license"
                                :selected-items="quotationLines.license"
                                :multiplier="settings.equipos"
                                @add-item="addItem"
                                @remove-item="removeItem"
                                @update-item="updateItem"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-additional variant="info">
                            Adicional
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('additional') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-additional" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <category-items-tab
                                category-slug="additional"
                                category-name="Adicional"
                                :items="categoryItems.additional"
                                :selected-items="quotationLines.additional"
                                :multiplier="settings.equipos"
                                @add-item="addItem"
                                @remove-item="removeItem"
                                @update-item="updateItem"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>
            </div>

            <div class="form-section mb-4">
                <h5>Totales</h5>
                <div class="totals-container p-3 bg-light rounded">
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Subtotal General:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ subtotalGeneral | currency }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>IVA 19%:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ iva | currency }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6 d-flex align-items-center">
                            <strong>Descuento:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <app-currency-input
                                v-model="quotation.discount"
                                label=""
                            />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <strong>Total (con descuento):</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong class="text-primary" style="font-size: 1.2em;">{{ total | currency }}</strong>
                        </div>
                    </div>
                    <hr class="my-2">
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Costo Total del Proyecto:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ totalCost | currency }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Horas de Instalación:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ installationHours.toFixed(2) }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Días de Instalación:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ installationDays.toFixed(2) }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Rentabilidad / Utilidad:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong :class="profitability >= 0 ? 'text-success' : 'text-danger'" style="font-size: 1.1em;">
                                {{ profitability | currency }}
                                <span v-if="profitabilityPercentage !== 0" class="ml-2">
                                    ({{ profitabilityPercentage >= 0 ? '+' : '' }}{{ profitabilityPercentage.toFixed(2) }}%)
                                </span>
                            </strong>
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-actions">
                <button class="btn btn-secondary" @click="cancel">
                    <i class="fa fa-times"></i> Cancelar
                </button>
                <button class="btn btn-primary" @click="updateQuotation" :disabled="saving">
                    <i class="fa fa-save"></i> {{ saving ? 'Guardando...' : 'Actualizar Cotización' }}
                </button>
            </div>
        </div>
        <div v-else class="text-center p-4">
            <p>Cargando cotización...</p>
        </div>
    </div>
</template>

<script>
import SubTitle from '../../UI/Layout/SubTitle.vue';
import FormSelectInput from '../../UI/Controls/Forms/FormSelectInput.vue';
import FormSelect2Input from '../../UI/Controls/Forms/FormSelect2Input.vue';
import FormTextInput from '../../UI/Controls/Forms/FormTextInput.vue';
import FormTextareaInput from '../../UI/Controls/Forms/FormTextareaInput.vue';
import CurrencyInput from '../../UI/Controls/Forms/CurrencyInput.vue';
import CategoryItemsTab from './CategoryItemsTab/CategoryItemsTab.vue';
import quotationsRepository from '../../../repositories/quotations.repository';
import kitsRepository from '../../../repositories/kits.repository';
import { required } from 'vuelidate/lib/validators';
import { getQuotationStatusOptions } from '../../../utils/quotationOptions';

export default {
    components: {
        appSubTitle: SubTitle,
        appFormSelectInput: FormSelectInput,
        appFormSelect2Input: FormSelect2Input,
        appFormTextInput: FormTextInput,
        appFormTextareaInput: FormTextareaInput,
        appCurrencyInput: CurrencyInput,
        categoryItemsTab: CategoryItemsTab
    },
    data() {
        return {
            quotation: {
                id: null,
                client_id: null,
                title: '',
                description: '',
                status: 'draft',
                discount: 0
            },
            clientOptions: [],
            selectedClient: null,
            categoryItems: {
                'equipment-cctv': [],
                'equipment-access-control': [],
                'equipment-network': [],
                'equipment-alarm': [],
                material: [],
                labor: [],
                license: [],
                additional: []
            },
            quotationLines: {
                'equipment-cctv': [],
                'equipment-access-control': [],
                'equipment-network': [],
                'equipment-alarm': [],
                material: [],
                labor: [],
                license: [],
                additional: []
            },
            settings: {},
            saving: false,
            loading: true,
            kits: [],
            selectedKitId: null
        };
    },
    validations: {
        quotation: {
            client_id: { required },
            title: { required }
        }
    },
    computed: {
        statusOptions() {
            return getQuotationStatusOptions();
        },
        kitOptions() {
            return this.kits.map(kit => ({
                id: kit.id,
                name: `${kit.name} (${kit.reference})`
            }));
        },
        subtotalGeneral() {
            let total = 0;
            Object.keys(this.quotationLines).forEach(category => {
                this.quotationLines[category].forEach(line => {
                    total += line.subtotal_after_multiplier || 0;
                });
            });
            return total;
        },
        salePrice() {
            const discount = parseFloat(this.quotation.discount) || 0;
            return Math.max(this.subtotalGeneral - discount, 0);
        },
        iva() {
            return this.salePrice * 0.19;
        },
        total() {
            return this.salePrice + this.iva;
        },
        totalCost() {
            let cost = 0;
            Object.keys(this.quotationLines).forEach(category => {
                this.quotationLines[category].forEach(line => {
                    if (line.subtotal !== undefined && line.subtotal !== null) {
                        cost += parseFloat(line.subtotal) || 0;
                    } else {
                        const quantity = line.quantity !== null && line.quantity !== undefined ? line.quantity : (line.days ?? 0);
                        const unitPrice = parseFloat(line.unit_price) || 0;
                        cost += quantity * unitPrice;
                    }
                });
            });
            return cost;
        },
        profitability() {
            return this.salePrice - this.totalCost;
        },
        profitabilityPercentage() {
            if (this.salePrice === 0) return 0;
            return (this.profitability / this.salePrice) * 100;
        },
        installationHours() {
            let hours = 0;
            Object.keys(this.quotationLines).forEach(category => {
                this.quotationLines[category].forEach(line => {
                    const isAutoLine = line.is_labor_of_item || line.isLaborLine || line.isAccesoriesLine;
                    if (!isAutoLine && line.item && line.item.installation_hours) {
                        const quantity = line.quantity || line.days || 1;
                        hours += parseFloat(line.item.installation_hours) * quantity;
                    }
                });
            });
            return hours;
        },
        installationDays() {
            return this.installationHours / 8;
        }
    },
    created() {
        this.loadClients();
        this.loadSettings();
        this.loadCategoryItems();
        this.loadKits();
        this.loadQuotation();
    },
    methods: {
        loadQuotation() {
            const id = this.$route.params.id;
            if (!id) {
                toastr.error('ID de cotización no válido');
                this.cancel();
                return;
            }

            this.loading = true;
            const pr = { ...quotationsRepository };
            pr.getQuotation(id)
                .then(response => {
                    if (response) {
                        this.quotation = {
                            id: response.id,
                            client_id: response.client_id,
                            title: response.title || '',
                            description: response.description || '',
                            status: response.status || 'draft',
                            discount: parseFloat(response.discount) || 0
                        };
                        
                        if (response.client) {
                            this.selectedClient = response.client;
                        }
                        this.organizeLinesByCategory(response.lines || []);
                    } else {
                        toastr.error('No se pudo cargar la cotización');
                        this.cancel();
                    }
                })
                .catch(error => {
                    toastr.error('Error al cargar la cotización');
                    console.error(error);
                    this.cancel();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        organizeLinesByCategory(lines) {
            const categories = [
                'equipment-cctv',
                'equipment-access-control',
                'equipment-network',
                'equipment-alarm',
                'material',
                'labor',
                'license',
                'additional'
            ];
            categories.forEach(categorySlug => {
                this.quotationLines[categorySlug] = [];
            });
            
            lines.forEach(line => {
                let categorySlug = line.category ? line.category.slug : null;
                
                if (categorySlug === 'equipment') {
                    categorySlug = 'equipment-cctv';
                }
                
                if (categorySlug && this.quotationLines[categorySlug] !== undefined) {
                    const isAutoLine = !line.item_id && !line.item_manual;
                    
                    let quantityValue = 1;

                    if (line.quantity !== null && line.quantity !== undefined) {
                        if (typeof line.quantity === 'number') {
                            quantityValue = line.quantity;
                        } else if (typeof line.quantity === 'string' && line.quantity !== '') {
                            const parsed = parseInt(line.quantity, 10);
                            quantityValue = isNaN(parsed) ? 1 : parsed;
                        }
                    }
                    
                    const editableLine = {
                        _uniqueId: line._uniqueId || `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        item_id: line.item_id,
                        item: line.item || null,
                        item_manual: line.item_manual || null,
                        source_item_id: line.source_item_id || null,
                        source_item: line.source_item || null,
                        original_item_id: line.source_item_id || null,
                        category_slug: categorySlug,
                        currency: line.currency,
                        unit_price: parseFloat(line.unit_price) || 0,
                        labor: line.item ? parseFloat(line.item.labor) || 0 : 0,
                        quantity: quantityValue,
                        days: null,
                        multiplier_used: parseFloat(line.multiplier_used) || 1,
                        subtotal: parseFloat(line.subtotal) || 0,
                        subtotal_after_multiplier: parseFloat(line.subtotal_after_multiplier) || 0,
                        labor_cost: 0,
                        subtotal_cop: parseFloat(line.subtotal_cop) || 0,
                        is_labor_of_item: !!line.is_labor_of_item,
                        isLaborLine: !!line.is_labor_of_item || (isAutoLine && categorySlug === 'labor'),
                        isAccesoriesLine: false
                    };

                    this.quotationLines[categorySlug].push(editableLine);
                }
            });
            
            this.syncAutoLinesAfterLoad();
        },
        syncAutoLinesAfterLoad() {
            // Sincronizar líneas de labor
            this.quotationLines.labor.forEach((laborLine, laborIndex) => {
                if (laborLine.isLaborLine && laborLine.original_item_id) {
                    let originalLine = null;
                    Object.keys(this.quotationLines).forEach(categorySlug => {
                        const found = this.quotationLines[categorySlug].find(
                            l => l.item_id === laborLine.original_item_id
                        );
                        if (found) {
                            originalLine = found;
                        }
                    });

                    if (originalLine) {
                        if (originalLine.quantity !== null && originalLine.quantity !== undefined) {
                            this.$set(laborLine, 'quantity', originalLine.quantity);
                            this.$set(laborLine, 'days', null);
                        }
                        this.calculateLineTotal('labor', laborIndex);
                    }
                }
            });

            // Sincronizar líneas de accesorios (ahora en materiales)
            this.quotationLines.material.forEach((accesoriesLine, accesoriesIndex) => {
                if (accesoriesLine.isAccesoriesLine && accesoriesLine.original_item_id) {
                    let originalLine = null;
                    Object.keys(this.quotationLines).forEach(categorySlug => {
                        const found = this.quotationLines[categorySlug].find(
                            l => l.item_id === accesoriesLine.original_item_id
                        );
                        if (found) {
                            originalLine = found;
                        }
                    });

                    if (originalLine) {
                        if (originalLine.quantity !== null && originalLine.quantity !== undefined) {
                            this.$set(accesoriesLine, 'quantity', originalLine.quantity);
                            this.$set(accesoriesLine, 'days', null);
                        }
                        this.calculateLineTotal('material', accesoriesIndex);
                    }
                }
            });
        },
        loadClients() {
            const pr = { ...quotationsRepository };
            pr.getClients()
                .then(response => {
                    if (response && Array.isArray(response)) {
                        this.clientOptions = response.map(client => ({
                            id: client.id,
                            name: client.name
                        }));
                    }
                })
                .catch(error => {
                    toastr.error('Error al cargar los clientes');
                    console.error(error);
                });
        },
        loadSettings() {
            const pr = { ...quotationsRepository };
            pr.getQuotationSettings()
                .then(response => {
                    if (response) {
                        const settingsObj = {};
                        if (Array.isArray(response)) {
                            response.forEach(setting => {
                                if (setting && setting.slug) {
                                    settingsObj[setting.slug] = parseFloat(setting.value) || 1;
                                }
                            });
                        } else {
                            Object.keys(response).forEach(key => {
                                const setting = response[key];
                                if (setting && setting.slug) {
                                    settingsObj[setting.slug] = parseFloat(setting.value) || 1;
                                }
                            });
                        }
                        this.settings = settingsObj;
                    }
                })
                .catch(error => {
                    console.error('Error al cargar configuración:', error);
                });
        },
        loadCategoryItems() {
            const categories = [
                'equipment-cctv',
                'equipment-access-control',
                'equipment-network',
                'equipment-alarm',
                'material',
                'labor',
                'license',
                'additional'
            ];
            categories.forEach(categorySlug => {
                const pr = { ...quotationsRepository };
                pr.getItemsByCategory(categorySlug)
                    .then(response => {
                        if (response && Array.isArray(response)) {
                            this.$set(this.categoryItems, categorySlug, response);
                        }
                    })
                    .catch(error => {
                        console.error(`Error al cargar items de ${categorySlug}:`, error);
                    });
            });
        },
        loadKits() {
            const pr = { ...kitsRepository };
            pr.getKits()
                .then(response => {
                    if (response && Array.isArray(response)) {
                        this.kits = response;
                    }
                })
                .catch(error => {
                    console.error('Error al cargar kits:', error);
                });
        },
        onKitSelected(kitId) {
            if (!kitId) {
                return;
            }

            const kit = this.kits.find(k => k.id === kitId);
            if (!kit || !kit.items || kit.items.length === 0) {
                toastr.warning('El kit seleccionado no tiene items');
                return;
            }

            let kitAlreadyAdded = false;

            Object.keys(this.quotationLines).forEach(categorySlug => {
                const hasKitLine = this.quotationLines[categorySlug].some(line => line.kit_id === kitId);
                if (hasKitLine) {
                    kitAlreadyAdded = true;
                }
            });

            kit.items.forEach(kitItem => {
                const item = kitItem;
                const quantity = kitItem.pivot ? kitItem.pivot.quantity : 1;
                const categorySlug = (item.category && item.category.slug) ? item.category.slug : 'equipment-cctv';

                if (kitAlreadyAdded) {
                    this.incrementKitItemQuantity(categorySlug, item.id, kitId, quantity);
                    if (item.labor && parseFloat(item.labor) > 0) {
                        this.incrementKitLaborQuantity(item.id, kitId, quantity);
                    }
                    if (categorySlug !== 'labor' && item.accesories && parseFloat(item.accesories) > 0) {
                        this.incrementKitAccesoriesQuantity(item.id, kitId, quantity);
                    }
                } else {
                    this.addItemFromKit({ categorySlug, item, quantity, kitId: kit.id });

                    if (item.labor && parseFloat(item.labor) > 0) {
                        this.addLaborFromKit({ item, quantity, kitId: kit.id });
                    }

                    if (categorySlug !== 'labor' && item.accesories && parseFloat(item.accesories) > 0) {
                        this.addAccesoriesFromKit({ item, quantity, kitId: kit.id });
                    }
                }
            });

            if (kitAlreadyAdded) {
                toastr.info(`Kit "${kit.name}" ya existe. Se incrementaron las cantidades de los items.`);
            } else {
                if (kit.technical_labor && parseFloat(kit.technical_labor) > 0) {
                    this.addTechnicalLaborFromKit(kit.technical_labor, kit.id);
                }

                if (kit.installation_unforeseen && parseFloat(kit.installation_unforeseen) > 0) {
                    this.addKitLaborLine('Imprevistos de instalación', kit.installation_unforeseen, kit.id);
                }

                if (kit.ladder_supply_transport && parseFloat(kit.ladder_supply_transport) > 0) {
                    this.addKitLaborLine('Suministro y transporte de escaleras requeridas x tiempo de instalación', kit.ladder_supply_transport, kit.id);
                }

                if (kit.installation_consumables && parseFloat(kit.installation_consumables) > 0) {
                    this.addKitMaterialLine('Consumibles de instalación (chazos, tornillos, amarras, etc)', kit.installation_consumables, kit.id);
                }

                if (kit.signage_fabrication && parseFloat(kit.signage_fabrication) > 0) {
                    this.addKitMaterialLine('Fabricación de señaliticas', kit.signage_fabrication, kit.id);
                }

                toastr.success(`Kit "${kit.name}" agregado exitosamente`);
            }

            this.selectedKitId = null;
        },
        incrementKitItemQuantity(categorySlug, itemId, kitId, quantityToAdd) {
            const existingLineIndex = this.quotationLines[categorySlug].findIndex(
                line => line.item_id === itemId && line.kit_id === kitId
            );

            if (existingLineIndex !== -1) {
                const line = this.quotationLines[categorySlug][existingLineIndex];
                line.quantity = (line.quantity || 0) + quantityToAdd;
                this.calculateLineTotal(categorySlug, existingLineIndex);
            } else {
                const item = this.categoryItems[categorySlug].find(i => i.id === itemId);
                if (item) {
                    this.addItemFromKit({ categorySlug, item, quantityToAdd, kitId });
                }
            }
        },
        incrementKitLaborQuantity(itemId, kitId, quantityToAdd) {
            const existingLineIndex = this.quotationLines.labor.findIndex(
                line => line.source_item_id === itemId && line.kit_id === kitId && line.is_labor_of_item
            );

            if (existingLineIndex !== -1) {
                const line = this.quotationLines.labor[existingLineIndex];
                line.quantity = (line.quantity || 0) + quantityToAdd;
                this.calculateLineTotal('labor', existingLineIndex);
            }
        },
        incrementKitAccesoriesQuantity(itemId, kitId, quantityToAdd) {
            const existingLineIndex = this.quotationLines.material.findIndex(
                line => line.source_item_id === itemId && line.kit_id === kitId && line.isAccesoriesLine
            );

            if (existingLineIndex !== -1) {
                const line = this.quotationLines.material[existingLineIndex];
                line.quantity = (line.quantity || 0) + quantityToAdd;
                this.calculateLineTotal('material', existingLineIndex);
            }
        },
        addItemFromKit({ categorySlug, item, quantity, kitId }) {
            const multiplier = this.getMultiplier(categorySlug);
            const line = {
                _uniqueId: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                item_id: item.id,
                item: item,
                kit_id: kitId || null,
                category_slug: categorySlug,
                currency: item.currency || 'COP',
                unit_price: parseFloat(item.unit_price) || 0,
                labor: parseFloat(item.labor) || 0,
                quantity: quantity,
                days: null,
                multiplier_used: multiplier,
                subtotal: 0,
                subtotal_after_multiplier: 0,
                labor_cost: 0,
                subtotal_cop: 0
            };
            this.quotationLines[categorySlug].unshift(line);
            this.calculateLineTotal(categorySlug, 0);
        },
        addLaborFromKit({ item, quantity, kitId }) {
            const laborMultiplier = this.getMultiplier('labor');
            const laborLine = {
                _uniqueId: `labor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                item_id: null,
                item: item,
                original_item_id: item.id,
                source_item_id: item.id,
                kit_id: kitId || null,
                is_labor_of_item: true,
                category_slug: 'labor',
                currency: 'COP',
                unit_price: parseFloat(item.labor) || 0,
                labor: 0,
                quantity: quantity,
                days: null,
                multiplier_used: laborMultiplier,
                subtotal: 0,
                subtotal_after_multiplier: 0,
                labor_cost: 0,
                subtotal_cop: 0,
                isLaborLine: true
            };
            this.quotationLines.labor.unshift(laborLine);
            this.calculateLineTotal('labor', 0);
        },
        addAccesoriesFromKit({ item, quantity, kitId }) {
            const materialMultiplier = this.getMultiplier('material');
            const accesoriesLine = {
                _uniqueId: `acc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                item_id: null,
                item: item,
                original_item_id: item.id,
                source_item_id: item.id,
                kit_id: kitId || null,
                is_labor_of_item: false,
                category_slug: 'material',
                currency: 'COP',
                unit_price: parseFloat(item.accesories) || 0,
                labor: 0,
                quantity: quantity,
                days: null,
                multiplier_used: materialMultiplier,
                subtotal: 0,
                subtotal_after_multiplier: 0,
                labor_cost: 0,
                subtotal_cop: 0,
                isAccesoriesLine: true
            };
            this.quotationLines.material.unshift(accesoriesLine);
            this.calculateLineTotal('material', 0);
        },
        addTechnicalLaborFromKit(technicalLabor, kitId) {
            const laborMultiplier = this.getMultiplier('labor');
            const technicalLaborLine = {
                _uniqueId: `tech-labor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                item_id: null,
                item: null,
                item_manual: 'Mano de obra técnica por instalación, configuración y puesta en marcha',
                original_item_id: null,
                source_item_id: null,
                kit_id: kitId || null,
                is_labor_of_item: false,
                category_slug: 'labor',
                currency: 'COP',
                unit_price: parseFloat(technicalLabor) || 0,
                labor: 0,
                quantity: 1,
                days: null,
                multiplier_used: laborMultiplier,
                subtotal: 0,
                subtotal_after_multiplier: 0,
                labor_cost: 0,
                subtotal_cop: 0,
                isLaborLine: false
            };
            this.quotationLines.labor.unshift(technicalLaborLine);
            this.calculateLineTotal('labor', 0);
        },
        addKitLaborLine(description, amount, kitId) {
            const laborMultiplier = this.getMultiplier('labor');
            const laborLine = {
                _uniqueId: `kit-labor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                item_id: null,
                item: null,
                item_manual: description,
                original_item_id: null,
                source_item_id: null,
                kit_id: kitId || null,
                is_labor_of_item: false,
                category_slug: 'labor',
                currency: 'COP',
                unit_price: parseFloat(amount) || 0,
                labor: 0,
                quantity: 1,
                days: null,
                multiplier_used: laborMultiplier,
                subtotal: 0,
                subtotal_after_multiplier: 0,
                labor_cost: 0,
                subtotal_cop: 0,
                isLaborLine: false
            };
            this.quotationLines.labor.unshift(laborLine);
            this.calculateLineTotal('labor', 0);
        },
        addKitMaterialLine(description, amount, kitId) {
            const materialMultiplier = this.getMultiplier('material');
            const materialLine = {
                _uniqueId: `kit-material-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                item_id: null,
                item: null,
                item_manual: description,
                original_item_id: null,
                source_item_id: null,
                kit_id: kitId || null,
                is_labor_of_item: false,
                category_slug: 'material',
                currency: 'COP',
                unit_price: parseFloat(amount) || 0,
                labor: 0,
                quantity: 1,
                days: null,
                multiplier_used: materialMultiplier,
                subtotal: 0,
                subtotal_after_multiplier: 0,
                labor_cost: 0,
                subtotal_cop: 0
            };
            this.quotationLines.material.unshift(materialLine);
            this.calculateLineTotal('material', 0);
        },
        onClientSelected(clientId) {
            if (clientId) {
                this.selectedClient = this.clientOptions.find(c => c.id === clientId) || null;
            } else {
                this.selectedClient = null;
            }
        },
        addItem({ categorySlug, item }) {
            const multiplier = this.getMultiplier(categorySlug);
            const line = {
                _uniqueId: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                item_id: item.id,
                item: item,
                category_slug: categorySlug,
                currency: item.currency || 'COP',
                unit_price: parseFloat(item.unit_price) || 0,
                labor: parseFloat(item.labor) || 0,
                quantity: 1,
                days: null,
                multiplier_used: multiplier,
                subtotal: 0,
                subtotal_after_multiplier: 0,
                labor_cost: 0,
                subtotal_cop: 0
            };
            this.quotationLines[categorySlug].unshift(line);
            this.calculateLineTotal(categorySlug, 0);
            
            if (item.labor && parseFloat(item.labor) > 0) {
                const laborMultiplier = this.getMultiplier('labor');
                const originalLine = this.quotationLines[categorySlug][0];
                const laborLine = {
                    _uniqueId: `labor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    item_id: null,
                    item: item,
                    original_item_id: item.id,
                    source_item_id: item.id,
                    is_labor_of_item: true,
                    category_slug: 'labor',
                    currency: 'COP',
                    unit_price: parseFloat(item.labor) || 0,
                    labor: 0,
                    quantity: originalLine ? (originalLine.quantity || null) : (useDays ? null : 1),
                    days: originalLine ? (originalLine.days || null) : (useDays ? 1 : null),
                    multiplier_used: laborMultiplier,
                    subtotal: 0,
                    subtotal_after_multiplier: 0,
                    labor_cost: 0,
                    subtotal_cop: 0,
                    isLaborLine: true
                };
                this.quotationLines.labor.unshift(laborLine);
                this.calculateLineTotal('labor', 0);
            }
            
            if (categorySlug !== 'labor' && item.accesories && parseFloat(item.accesories) > 0) {
                const existingAccesoriesLine = this.quotationLines.material.find(
                    line => line.original_item_id === item.id && line.isAccesoriesLine
                );
                
                if (!existingAccesoriesLine) {
                    const materialMultiplier = this.getMultiplier('material');
                    const originalLine = this.quotationLines[categorySlug][0];
                    const accesoriesLine = {
                        _uniqueId: `acc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        item_id: null,
                        item: item,
                        original_item_id: item.id,
                        source_item_id: item.id,
                        category_slug: 'material',
                        currency: 'COP',
                        unit_price: parseFloat(item.accesories) || 0,
                        labor: 0,
                        quantity: originalLine ? (originalLine.quantity || null) : (useDays ? null : 1),
                        days: originalLine ? (originalLine.days || null) : (useDays ? 1 : null),
                        multiplier_used: materialMultiplier,
                        subtotal: 0,
                        subtotal_after_multiplier: 0,
                        labor_cost: 0,
                        subtotal_cop: 0,
                        isAccesoriesLine: true
                    };

                    this.quotationLines.material.unshift(accesoriesLine);
                    const accesoriesIndex = 0;
                    this.calculateLineTotal('material', accesoriesIndex);
                }
            }
        },
        removeItem({ categorySlug, index }) {
            const removedLine = this.quotationLines[categorySlug][index];
            this.quotationLines[categorySlug].splice(index, 1);
            
            if (removedLine && removedLine.item_id) {
                const laborLineIndex = this.quotationLines.labor.findIndex(
                    line => line.original_item_id === removedLine.item_id && line.isLaborLine
                );
                if (laborLineIndex !== -1) {
                    this.quotationLines.labor.splice(laborLineIndex, 1);
                }
            }
            
            if (removedLine && removedLine.item_id) {
                const accesoriesLineIndex = this.quotationLines.material.findIndex(
                    line => line.original_item_id === removedLine.item_id && line.isAccesoriesLine
                );
                if (accesoriesLineIndex !== -1) {
                    this.quotationLines.material.splice(accesoriesLineIndex, 1);
                }
            }
        },
        updateItem({ index, categorySlug, line, quantity, item, hasLabor, useDays }) {
            this.calculateLineTotal(categorySlug, index);

            if (item && item.id) {
                const useDaysFlag = useDays !== undefined ? useDays : (line.days !== null && line.days !== undefined);
                this.syncLaborLine(item.id, quantity || line.days, useDaysFlag);
                this.syncAccesoriesLine(item.id, quantity || line.days, useDaysFlag);
            }
        },
        addManualLaborItem({ description, quantity, unitPrice }) {
            const multiplier = this.getMultiplier('labor');
            const line = {
                item_id: null,
                item: null,
                item_manual: description,
                category_slug: 'labor',
                currency: 'COP',
                unit_price: parseFloat(unitPrice) || 0,
                labor: 0,
                quantity: parseInt(quantity) || 1,
                days: null,
                multiplier_used: multiplier,
                subtotal: 0,
                subtotal_after_multiplier: 0,
                labor_cost: 0,
                subtotal_cop: 0
            };
            this.quotationLines.labor.unshift(line);
            this.calculateLineTotal('labor', 0);
        },
        syncLaborLine(itemId, quantity, useDays = false) {
            const laborLineIndex = this.quotationLines.labor.findIndex(
                line => line.original_item_id === itemId && line.isLaborLine
            );
            
            if (laborLineIndex !== -1) {
                const laborLine = this.quotationLines.labor[laborLineIndex];
                if (useDays) {
                    laborLine.days = quantity;
                    laborLine.quantity = null;
                } else {
                    laborLine.quantity = quantity;
                    laborLine.days = null;
                }
                this.calculateLineTotal('labor', laborLineIndex);
            }
        },
        syncAccesoriesLine(itemId, quantity, useDays = false) {
            const accesoriesLineIndex = this.quotationLines.material.findIndex(
                line => line.original_item_id === itemId && line.isAccesoriesLine
            );
            
            if (accesoriesLineIndex !== -1) {
                const accesoriesLine = this.quotationLines.material[accesoriesLineIndex];
                if (useDays) {
                    accesoriesLine.days = quantity;
                    accesoriesLine.quantity = null;
                } else {
                    accesoriesLine.quantity = quantity;
                    accesoriesLine.days = null;
                }
                this.calculateLineTotal('material', accesoriesLineIndex);
            }
        },
        calculateLineTotal(categorySlug, index) {
            const line = this.quotationLines[categorySlug][index];
            if (!line) return;

            const quantityValue = line.quantity !== null && line.quantity !== undefined ? line.quantity : 0;
            const unitPrice = parseFloat(line.unit_price) || 0;
            const multiplier = parseFloat(line.multiplier_used) || 1;
            const labor = parseFloat(line.labor) || 0;

            if (line.isLaborLine) {
                line.subtotal = quantityValue * unitPrice;
                line.subtotal_after_multiplier = line.subtotal / multiplier;
                line.labor_cost = 0;
                line.subtotal_cop = line.subtotal_after_multiplier;
            } else if (line.isAccesoriesLine) {
                line.subtotal = quantityValue * unitPrice;
                line.subtotal_after_multiplier = line.subtotal / multiplier;
                line.labor_cost = 0;
                line.subtotal_cop = line.subtotal_after_multiplier;
            } else {
                line.subtotal = quantityValue * unitPrice;
                line.subtotal_after_multiplier = line.subtotal / multiplier;
                line.labor_cost = 0;
                line.subtotal_cop = line.subtotal_after_multiplier;
            }
        },
        getMultiplier(categorySlug) {
            if (categorySlug === 'labor') {
                return parseFloat(this.settings['mano-obra']) || 0.70;
            } else if (categorySlug === 'material') {
                return parseFloat(this.settings.materiales) || 0.70;
            } else {
                return parseFloat(this.settings.equipos) || 0.70;
            }
        },
        getCategorySubtotal(categorySlug) {
            let total = 0;
            if (this.quotationLines[categorySlug]) {
                this.quotationLines[categorySlug].forEach(line => {
                    total += line.subtotal_after_multiplier || 0;
                });
            }
            return total;
        },
        cancel() {
            this.$router.push({ name: 'quotationsPage' });
        },
        updateQuotation() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                toastr.error('Por favor complete todos los campos requeridos');
                return;
            }

            const lines = [];
            let trmUsed = null;
            
            Object.keys(this.quotationLines).forEach(categorySlug => {
                this.quotationLines[categorySlug].forEach(line => {
                    if (line.quantity !== null && line.quantity !== undefined && line.quantity > 0) {
                        if (line.currency === 'USD' && trmUsed === null) {
                            trmUsed = parseFloat(this.settings.trm) || null;
                        }
                        
                        const lineCategorySlug = line.category_slug || categorySlug;
                        
                        let quantityValue = line.quantity;
                        
                        if (!line.item_id && line.original_item_id) {
                            let originalLine = null;
                            Object.keys(this.quotationLines).forEach(catSlug => {
                                const found = this.quotationLines[catSlug].find(
                                    l => l.item_id === line.original_item_id
                                );
                                if (found) {
                                    originalLine = found;
                                }
                            });
                            
                            if (originalLine && originalLine.quantity !== null && originalLine.quantity !== undefined) {
                                quantityValue = originalLine.quantity;
                            }
                        }

                        lines.push({
                            item_id: line.item_id || null,
                            category_slug: lineCategorySlug,
                            item_manual: line.item_manual || null,
                            source_item_id: line.source_item_id || null,
                            is_labor_of_item: !!line.is_labor_of_item,
                            currency: line.currency,
                            unit_price: line.unit_price,
                            multiplier_used: line.multiplier_used,
                            quantity: quantityValue,
                            days: null,
                            trm_used: line.currency === 'USD' ? trmUsed : null
                        });
                    }
                });
            });

            if (lines.length === 0) {
                toastr.error('Debe agregar al menos un item a la cotización');
                return;
            }

            this.saving = true;
            const payload = {
                ...this.quotation,
                trm_used: trmUsed,
                lines: lines
            };

            const pr = { ...quotationsRepository };
            pr.updateQuotation(payload, this.quotation.id)
                .then(response => {
                    if (response && response.status) {
                        toastr.success(response.msg || 'Cotización actualizada exitosamente');
                        this.$router.push({ name: 'quotationsPage' });
                    } else {
                        toastr.error(response?.msg || 'Error al actualizar la cotización');
                    }
                })
                .catch(error => {
                    toastr.error('Error al actualizar la cotización');
                    console.error(error);
                })
                .finally(() => {
                    this.saving = false;
                });
        }
    },
    filters: {
        currency(value) {
            if (!value && value !== 0) return '$ 0,00';
            return new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        }
    }
};
</script>

<style scoped>
.quotation-edit-page {
    padding: 20px;
}

.form-section {
    background: #fff;
    padding: 20px;
    border-radius: 5px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.client-info {
    border: 1px solid #dee2e6;
}

.totals-container {
    border: 2px solid #007bff;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}
</style>

